import { I18n } from '@calendly/i18n'
import { BareButton } from '@calendly/ui/components/bare-button'
import {
  DescriptionList,
  DescriptionLabel,
  DescriptionValue,
  Row,
} from '@calendly/ui/components/description-list'
import {
  CalendarIcon,
  TimeClockCircleIcon,
  LocationPinIcon,
  UserSingleIcon,
} from '@calendly/ui/components/icons'
import { BodyText, TitleText } from '@calendly/ui/components/typography'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'

import { useProductRefreshAppShell } from 'features/app_shell/product_refresh/useProductRefreshAppShell'
import { getMeetingsUserMePath } from 'features/meeting_intelligence'

import styles from './MeetingHoverCard.module.css'
import { formatAttendeesList } from '../utils'

/**
 * MeetingHoverCard component displays detailed meeting information in a card format.
 *
 * This component shows meeting information including:
 * - Calendar icon and title
 * - Meeting details (datetime, location, attendees)
 * - Optional additional content section (e.g., recap summary)
 *
 * @param {Object} props - Component props
 * @param {string} props.title - The meeting title
 * @param {string} [props.datetimeFormatted] - Full formatted date and time (e.g., "Wed, November 6, 2025, 3 – 3:45 PM")
 * @param {string} [props.location] - Meeting location (e.g., "Zoom")
 * @param {string[]} [props.attendees] - Array of attendee names/emails
 * @param {React.ReactNode} [props.additionalContent] - Additional content section component (e.g., recap summary)
 * @param {string} [props.meetingId] - The meeting ID used to link to the meeting details page
 *
 * @returns {JSX.Element} A card component displaying meeting information
 *
 * @example
 * ```jsx
 * <MeetingHoverCard
 *   title="John / Veronica - AI Design System Discussion"
 *   datetimeFormatted="Wed, November 6, 2025, 3 – 3:45 PM"
 *   location="Zoom"
 *   attendees={['Aaron Guiles', 'Jane Smith', 'Bob Johnson']}
 *   meetingId="28d0d73b-5bd8-4970-a854-ec8559a47320"
 * />
 * ```
 */
export const MeetingHoverCard = ({
  title,
  datetimeFormatted = '',
  location = '',
  attendees = [],
  additionalContent = null,
  meetingId = '',
}) => {
  const isProductRefreshAppShell = useProductRefreshAppShell()
  const attendeesContainerRef = useRef(null)
  const [maxChars, setMaxChars] = useState(40)

  // Dynamically calculate max characters based on container width
  useEffect(() => {
    if (attendeesContainerRef.current) {
      // Approximate character width based on font size (~7px per char for 14px body text)
      const containerWidth = attendeesContainerRef.current.offsetWidth
      const estimatedCharsPerLine = Math.floor(containerWidth / 7)
      setMaxChars(Math.max(20, estimatedCharsPerLine))
    }
  }, [])

  const { displayText, remainingCount } = formatAttendeesList(attendees, maxChars)

  return (
    <>
      {/* Header Section */}
      <div className={styles.header}>
        <div className={styles.iconBadge}>
          <CalendarIcon />
        </div>
        <div className={styles.headerText}>
          <TitleText size="small">{title}</TitleText>
          <BareButton
            onClick={
              () => window.open(`${getMeetingsUserMePath(isProductRefreshAppShell)}?selected=${meetingId}`, '_blank', 'noopener,noreferrer'
              )
            }
            className={styles.viewMeetingLink}
          >
            {I18n.t('publisher.agent_hub.recap_hover_card.view_meeting')}
          </BareButton>
        </div>
      </div>

      {/* Divider */}
      <div className={styles.divider} />

      {/* Content Section */}
      <div className={styles.content}>
        {/* Meeting Attributes */}
        <div>
          <DescriptionList format="grid" data-testid="contact-info-content">
            {datetimeFormatted && (
              <Row>
                <DescriptionLabel icon={<TimeClockCircleIcon size="standard" />} />
                <DescriptionValue>
                  <BodyText>{datetimeFormatted}</BodyText>
                </DescriptionValue>
              </Row>
            )}
            {location && (
              <Row>
                <DescriptionLabel icon={<LocationPinIcon size="standard" />} />
                <DescriptionValue ellipsis={false}>
                  <BodyText>{location}</BodyText>
                </DescriptionValue>
              </Row>
            )}
            {attendees && attendees.length > 0 && (
              <Row>
                <DescriptionLabel icon={<UserSingleIcon size="standard" />} />
                <DescriptionValue>
                  <div ref={attendeesContainerRef}>
                    <BodyText>
                      {displayText}
                      {remainingCount > 0 && (
                        <span>
                          {I18n.t('publisher.agent_hub.recap_hover_card.attendees_more', { count: remainingCount })}
                        </span>
                      )}
                    </BodyText>
                  </div>
                </DescriptionValue>
              </Row>
            )}
          </DescriptionList>
        </div>

        {/* Additional Content Section */}
        {additionalContent}
      </div>
    </>
  )
}

MeetingHoverCard.propTypes = {
  title: PropTypes.string.isRequired,
  datetimeFormatted: PropTypes.string,
  location: PropTypes.string,
  attendees: PropTypes.arrayOf(PropTypes.string),
  additionalContent: PropTypes.node,
  meetingId: PropTypes.string,
}
